import moment from "moment";

import { cloneDeep, forOwn, isEqual, mergeWith, pickBy } from "lodash";
import prettyMilliseconds from "pretty-ms";

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

const hasValidReactAppEnv = () =>
{
    // test if process.env.REACT_APP_ENV has a know value (not over null/undefined)

    switch(process.env.REACT_APP_ENV)
    {
    case utils.ENV_DEVELOPMENT:
    case utils.ENV_PRODUCTION:
    case utils.ENV_TEST:
        return true;
    }

    return false;
};

const arrayGetFirstDefinedImpl = (values, defaultValue) =>
{
    for(let i = 0; i < values.length; ++i)
    {
        const value = values[i];
        if(value !== undefined)
            return value;
    }

    return defaultValue;
};

const sequentialImpl = (array, callback) =>
{
    return new Promise((resolve, reject) =>
    {
        if(array.length === 0)
        {
            resolve();
            return;
        }

        callback(array.shift())
            .then(() => sequentialImpl(array, callback))
            .then(resolve)
            .catch(reject);
    });
};

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

const utils =
{
    ENV_DEVELOPMENT: "development",
    ENV_PRODUCTION: "production",
    ENV_TEST: "test",

    boolCast: (value, defValue = false) =>
    {
        if(value === undefined || value === null)
            return defValue;

        if(typeof value === "boolean")
            return value;

        if(typeof value === "string")
        {
            if(utils.isStringEmpty(value))
                return defValue;

            if(utils.equalsIgnoreCase(value, "false"))
                return false;

            return true;
        }

        return (!!value);
    },

    boolToInt: value => utils.boolCast(value) ? 1 : 0,

    toString: (value, defValue = "") =>
    {
        if(!utils.hasValue(value))
            return defValue;

        if(typeof value === "string")
            return value;

        return value.toString();
    },

    isBool: value => typeof value === "boolean",

    isNumber: value =>
    {
        if(typeof value !== "number")
            return false;

        if(value !== Number(value))
            return false;

        if(value === Infinity || value === !Infinity)
            return false;

        return true;
    },

    isInt: value => utils.isNumber(value) && (value % 1 === 0),
    isFloat: value => utils.isNumber(value) && (value % 1 !== 0),

    isString: value => typeof value === "string",

    isArray: value => Array.isArray(value),

    isArrayOf: (value, validator, allowEmpty = true) =>
    {
        if(!utils.isArray(value))
            return false;

        if(!allowEmpty && value.length === 0)
            return false;

        return value.every(validator);
    },

    isBoolArray: (value, allowEmpty = true) => utils.isArrayOf(value, utils.isBool, allowEmpty),
    isNumberArray: (value, allowEmpty = true) => utils.isArrayOf(value, utils.isNumber, allowEmpty),
    isIntArray: (value, allowEmpty = true) => utils.isArrayOf(value, utils.isInt, allowEmpty),
    isFloatArray: (value, allowEmpty = true) => utils.isArrayOf(value, utils.isFloat, allowEmpty),
    isStringArray: (value, allowEmpty = true) => utils.isArrayOf(value, utils.isString, allowEmpty),
    isBufferArray: (value, allowEmpty = true) => utils.isArrayOf(value, utils.isBuffer, allowEmpty),
    isObjectArray: (value, allowEmpty = true) => utils.isArrayOf(value, utils.isObject, allowEmpty),

    isBuffer: value => Buffer.isBuffer(value),

    isObject: value => utils.hasValue(value) && !utils.isArray(value) && (typeof value === "object"),
    isFunction: value => value instanceof Function,

    combineStrings: (first, second, separator = " ") =>
    {
        if(utils.isStringEmpty(first))
            return second;

        if(utils.isStringEmpty(second))
            return first;

        return first + separator + second;
    },

    hasValue: v => (v !== undefined) && (v !== null),

    isBrowser: () => typeof window !== "undefined",

    isDevelopmentEnvironment: () =>
    {
        if(process.env.REACT_APP_ENV === utils.ENV_DEVELOPMENT)
            return true;

        return (!utils.isProductionEnvironment() && !utils.isTestEnvironment());
    },

    isProductionEnvironment: () =>
    {
        if(process.env.REACT_APP_ENV === utils.ENV_PRODUCTION)
            return true;

        return !hasValidReactAppEnv() && (process.env.NODE_ENV === utils.ENV_PRODUCTION);
    },

    isTestEnvironment: () =>
    {
        if(process.env.REACT_APP_ENV === utils.ENV_TEST)
            return true;

        return !hasValidReactAppEnv() && (process.env.NODE_ENV === utils.ENV_TEST);
    },

    getEnvironmentName: () => utils.isDevelopmentEnvironment() ? "development" : utils.valueOrDefault(process.env.NODE_ENV, "unknown"),

    isNode: () =>
    {
        // http://stackoverflow.com/questions/4224606/how-to-check-whether-a-script-is-running-under-node-js
        return (typeof process !== "undefined") && (process.release) && (process.release.name === "node");
    },

    merge: (...sources) =>
    {
        return mergeWith({}, ...sources, (objValue, srcValue) =>
        {
            // lodash's merge doesn't concat arrays!
            if(utils.isArray(objValue))
                return objValue.concat(srcValue);

            return undefined;   // Default "merge" behaviour
        });
    },

    objectIsEmpty: obj =>
    {
        if(!utils.hasValue(obj))
            return true;

        return Object.keys(obj).length === 0;
    },

    objectHasKey: (obj, key) =>
    {
        if(!utils.hasValue(obj))
            return false;

        return obj.hasOwnProperty(key);
    },

    objectFilter: (obj, callback) =>
    {
        if(!utils.hasValue(obj))
            return obj;

        return pickBy(obj, callback);
    },

    // callback = (value, key) returning false to break the loop
    objectIterate: (obj, callback) =>
    {
        if(utils.hasValue(obj))
            forOwn(obj, callback);
    },

    // callback = (value, key) returning "undefined" to break the loop
    objectMap: (obj, callback) =>
    {
        if(!utils.hasValue(obj))
            return null;

        const result = [];

        utils.objectIterate(obj, (value, key) =>
        {
            const entry = callback(value, key);
            if(entry === undefined)
                return false;

            result.push(entry);
            return true;
        });

        return result;
    },

    deepClone: value => cloneDeep(value),

    objectEnsureKey: (object, key, defaultValue = null) =>
    {
        let value = object[key];
        // Only check for undefined values since null could be intentional
        if(value === undefined)
        {
            value = defaultValue;
            object[key] = value;
        }

        return value;
    },

    objectGetKey: (object, key, defaultValue = undefined) =>
    {
        if(!utils.hasValue(object))
            return defaultValue;

        let value = object[key];
        // Only check for undefined values since null could be intentional
        return value === undefined ? defaultValue : value;
    },

    objectTryKeys: (object, keys, defaultValue = undefined) =>
    {
        if(!utils.hasValue(object))
            return defaultValue;

        if(!utils.isArray(keys))
            keys = [keys];

        return arrayGetFirstDefinedImpl(keys.map(k => object[k]), defaultValue);
    },

    arrayGetFirstDefined: (values, defaultValue = undefined) =>
    {
        if(!utils.hasValue(values))
            return defaultValue;

        return arrayGetFirstDefinedImpl(values, defaultValue);
    },

    isStringEmpty: str => (!utils.hasValue(str)) || (str.length === 0),
    isArrayEmpty: arr => (!utils.hasValue(arr)) || (arr.length === 0),

    join: ({ separator = "", prefix = false, postfix = false }, ...args) =>
    {
        if(args.length === 0)
            return (prefix || postfix ? separator : "");

        const array = Array.from(args);
        return (prefix ? separator : "") + array.join(separator) + (postfix ? separator : "");
    },

    toInt: (str, defValue = 0) =>
    {
        str = utils.trimString(str);

        if(!utils.isStringEmpty(str))
        {
            const val = parseInt(str, 10);
            if(!isNaN(val))
                return val;
        }

        return defValue;
    },

    trimString: str =>
    {
        if(!utils.hasValue(str))
            return str;

        return str.trim();
    },

    capitalizeString: str =>
    {
        if(!utils.hasValue(str))
            return str;

        return str.charAt(0).toUpperCase() + str.slice(1);
    },

    equals: (v1, v2) => isEqual(v1, v2),

    equalsIgnoreCase: (str1, str2) =>
    {
        if(typeof str1 !== "string" || typeof str2 !== "string")
            return str1 === str2;

        return str1.toLowerCase() === str2.toLowerCase();
    },

    containsIgnoreCase: (str, value) => utils.hasValue(str) ? str.toLowerCase().includes(value.toLowerCase()) : false,

    startsWithIgnoreCase: (str, value) => utils.hasValue(str) ? str.toLowerCase().startsWith(value.toLowerCase()) : false,

    // Returns the number of milliseconds since epoch Jan 1, 1970 UTC 00:00
    getMillisecondsSinceEpoch: (date = null) => date !== null ? date.getTime() : Date.now(),

    getSecondsSinceEpoch: (date = null) => Math.round(utils.getMillisecondsSinceEpoch(date) / 1000.0),

    getDateLocal: () => new Date(),

    getDateUTC: () => utils.millisecondsToDateUTC(utils.getMillisecondsSinceEpoch()),

    secondsToDateUTC: seconds => utils.millisecondsToDateUTC(seconds * 1000),

    millisecondsToDateUTC: milliseconds =>
    {
        const utc_mm = moment(milliseconds).utc();
        return utc_mm.toDate();
    },

    secondsToDateLocal: seconds => utils.millisecondsToDateUTC(seconds * 1000),

    millisecondsToDateLocal: milliseconds =>
    {
        const local_mm = moment(milliseconds);
        return local_mm.toDate();
    },

    truncateDateTime: dt => new Date(dt.toDateString()),

    arrayToString: array =>
    {
        let str = "";

        for(let i = 0; i < array.length; ++i)
        {
            if(array[i] === 0)
                break;

            str += String.fromCharCode(array[i]);
        }

        return str;
    },

    arrayRemoveByValue: (array, value) =>
    {
        const index = array.indexOf(value);
        if(index !== -1)
        {
            array.splice(index, 1);
            return true;
        }

        return false;
    },

    arrayRemoveByIndex: (array, index) => array.splice(index, 1),      // Returns the array of the removed elements

    capitalizeFirstLetter: str =>
    {
        if(str.length === 0)
            return str;

        return str.charAt(0).toUpperCase() + str.slice(1);
    },

    capitalizeAllWords: (str, separator = " ") =>
    {
        if(str.length === 0)
            return str;

        const pieces = str.split(separator);
        for(let i = 0; i < pieces.length; i++)
        {
            pieces[i] = utils.capitalizeFirstLetter(pieces[i]);
        }

        return pieces.join(separator);
    },

    parsePort: (val, defValue = 0) =>
    {
        const port = parseInt(val, 10);
        if(isNaN(port))
            return defValue;

        if(port >= 0)
            return port;

        return defValue;
    },

    getErrorMessage: err =>
    {
        if(!utils.hasValue(err))
            return "";

        if(utils.hasValue(err.message))
            return err.message;

        return err;
    },

    unimplemented: (funcName, className = "") =>
    {
        throw new Error(`'${className !== "" ? (className + "::" + funcName + "' method") : (funcName + "' function") } unimplemented`);
    },

    formatMilliseconds: (milliseconds, options = { verbose: true }) => prettyMilliseconds(milliseconds, options),

    stringToObject: (str, keysSeparator = ";", valuesSeparator = "=") =>
    {
        return str.split(keysSeparator).reduce((result, token) => {
            const [key, value] = token.split(valuesSeparator);
            result[key] = value;
            return result;
        }, {});
    },

    stringToArray: (str, separator = ",") =>
    {
        if(!utils.hasValue(str))
            return [];

        return str.split(separator);
    },

    replaceAll: (str, prev, next) =>
    {
        // Do not use this form for compatibility
        //return str?.replaceAll(prev, next);
        return str?.replace(new RegExp(prev, "g"), next);
    },

    singularOrPlural: (count, singularForm, pluralForm) => count === 1 ? pluralForm : singularForm,

    arrayFilterDuplicates: (array, findIndexCallback = null) =>
    {
        if(!findIndexCallback)
            findIndexCallback = (a, v) => a.indexOf(v);

        return array.filter((item, index) => findIndexCallback(array, item) === index);
    },

    extractUserEmail: email =>
    {
        if(utils.isStringEmpty(email))
            return email;

        const pos = email.indexOf("@");
        if(pos === -1)
            return email;

        return email.substr(0, pos);
    },

    validateEmail: email =>
    {
        const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        return re.test(String(email).toLowerCase());
    },

    clamp: (num, min, max) => Math.min(Math.max(num, min), max),

    randomInt: (min = Number.MIN_SAFE_INTEGER, max = Number.MAX_SAFE_INTEGER) =>
    {
        // min, max are inclusive

        //min = Math.ceil(min);
        //max = Math.floor(max);
        return Math.floor(Math.random() * (max - min + 1)) + min;
    },

    randomUint: (max = Number.MAX_SAFE_INTEGER) => utils.uintCast(utils.randomInt(0, max)),

    raiseError: message => { throw new Error(message); },
    raiseErrorIf: (cond, message) => {
        if(cond)
            utils.raiseError(message);
    },

    jsonEncode: data => JSON.stringify(data),
    jsonDecode: (data, safe = true, defValue = null) =>
    {
        try
        {
            return JSON.parse(data);
        }
        catch(e)
        {
            if(!safe)
                throw e;
        }

        return defValue;
    },

    tryInvoke: (callback, ...args) =>
    {
        if(utils.hasValue(callback))
            callback(...args);
    },

    sequential: (array, callback) =>
    {
        if(!utils.hasValue(array))
            return Promise.resolve();

        return sequentialImpl(array, callback);
    },

    uintCast: v => v >>> 0,      // Cast to unsigned int :<>

    valueOrDefault: (value, defValue) => utils.hasValue(value) ? value : defValue,
    valueOrNull: value => utils.valueOrDefault(value, null),
    valueOrUndefined: value => utils.valueOrDefault(value, undefined)
};

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export default utils;

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

class Interface
{
    constructor(implementation = null)
    {
        this._implementation = implementation ? implementation : this.constructor.name;
    }

    _unimplemented = method => utils.unimplemented(method, this._implementation);
}

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export { Interface };

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
